<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { Toast } from "bootstrap";
import { useRoute } from "vue-router";
import type { DocId, ChangeSet, RestoreChangeSet } from "@/types";
import {
  useAuditDocumentTypes,
  useAuditDocumentIds,
  useAuditChangeSets,
  useAuditChangeSetDiff,
  useRestoreChangeset,
} from "@/queries/audit";

import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";

import * as monaco from "monaco-editor";

import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

const route = useRoute();

const populateLatest = ref(false);

const diffShow = ref(false);

const show = ref(false);

const documentType = ref("");
const documentId = ref<DocId | null>(null);
const selectedChangeSets = ref([] as ChangeSet[]);

const leftDocId = ref("");
const rightDocId = ref("");

const leftHeading = ref("");
const leftHeadingHelpText = ref("");

const rightHeading = ref("");
const rightHeadingHelpText = ref("");

const leftRestoreShow = ref(true);
const rightRestoreDisabled = ref(false);
const rightRestoreText = ref("Restore");
const rightRestoreShow = ref(true);
const rightNumBehindTrunk = ref(0);
const leftNumBehindTrunk = ref(0);

const { documentTypes, isLoadingDocumentTypes } = useAuditDocumentTypes();
const { documentIds, isLoadingDocumentIds } = useAuditDocumentIds(documentType);
const documentIdStr = ref("");
const { changeSets, isLoadingChangeSets } = useAuditChangeSets(documentType, documentIdStr);
const { leftJson, leftMetadata, rightJson, rightMetadata, isLoadingChangeSetDiff } = useAuditChangeSetDiff(documentType, documentIdStr, leftDocId, rightDocId);
const { restoreChangeset } = useRestoreChangeset();

onMounted(() => {
  show.value = true;
  if (route.query.populateLatest != undefined) {
    populateLatest.value = route.query.populateLatest === "true";
  }
  if (route.query.documentType != undefined) {
    documentType.value = route.query.documentType as string;
  }
});

// When documentIds load and a documentId is in the URL, auto-select it
watch(documentIds, (ids) => {
  const docIdFromUrl = route.query.documentId as string | undefined;
  if (docIdFromUrl && ids.length > 0 && !documentId.value) {
    const found = ids.find((x) => x.id == docIdFromUrl);
    if (found) {
      documentId.value = found;
      documentIdStr.value = found.id;
    }
  }
});

// When changeSets load and populateLatest is true, auto-populate selection
watch(changeSets, (sets) => {
  if (populateLatest.value && sets.length > 1 && selectedChangeSets.value.length === 0) {
    selectedChangeSets.value = [sets[0], sets[1]];
    selectedChangeSetChanged(null);
  }
});

function openDocumentTypeMultiSelect(_e: any) {
  // TQ manages the cache; no manual refetch needed
}

function documentTypeChanged(_e: any) {
  show.value = true;
  diffShow.value = false;
  documentId.value = null;
  documentIdStr.value = "";
  selectedChangeSets.value = [];
  // TQ auto-refetches documentIds when documentType changes
}

function openDocumentIdMultiSelect(_e: any) {
  // TQ manages the cache; no manual refetch needed
}

function documentIdChanged(_e: any) {
  show.value = true;
  selectedChangeSets.value = [];
  diffShow.value = false;

  const id = documentId.value?.id ?? "";
  documentIdStr.value = id;

  // Update URL with query params
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("documentType", documentType.value);
  searchParams.set("documentId", id);
  searchParams.set("populateLatest", populateLatest.value ? "true" : "false");
  history.pushState(null, "", window.location.pathname + "?" + searchParams.toString());
  // TQ auto-refetches changeSets when documentIdStr changes
}

function openChangesetMultiSelect(_e: any) {
  // TQ manages the cache; no manual refetch needed
}

async function selectedChangeSetChanged(e: any) {
  if (selectedChangeSets.value.length == 1 && changeSets.value.length == 1) {
    selectedChangeSets.value[1] = selectedChangeSets.value[0];
    leftRestoreShow.value = false;
  } else {
    leftRestoreShow.value = true;
  }

  if (
    selectedChangeSets.value.length > 1 &&
    selectedChangeSets.value.length < 3
  ) {
    show.value = true;

    (document.getElementById("changesetSelect") as HTMLElement).blur();

    //sort selection tos that the latest changeset is on the right pane.
    selectedChangeSets.value.sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    );
    leftDocId.value = selectedChangeSets.value[1].id;
    rightDocId.value = selectedChangeSets.value[0].id;

    if (rightDocId.value == changeSets.value[0].id) {
      rightRestoreDisabled.value = true;
      rightRestoreText.value = "Current";
      rightRestoreShow.value = false;
    } else {
      rightRestoreDisabled.value = false;
      rightRestoreText.value = "Restore";
      rightRestoreShow.value = true;
    }

    rightNumBehindTrunk.value = changeSets.value.findIndex(
      (x) => x.id == rightDocId.value
    );
    leftNumBehindTrunk.value = changeSets.value.findIndex(
      (x) => x.id == leftDocId.value
    );

    // TQ auto-fetches diff when leftDocId/rightDocId change
    // Monaco editor is set up in a watch on leftJson (below)

    leftHeading.value = selectedChangeSets.value[1].label;
    leftHeadingHelpText.value = `(${leftNumBehindTrunk.value} behind)`;

    rightHeading.value = selectedChangeSets.value[0].label;
    rightHeadingHelpText.value =
      rightNumBehindTrunk.value > 0
        ? ` (${rightNumBehindTrunk.value} behind)`
        : " (Current)";
  }
}

// Render Monaco diff editor when TQ loads the diff data
watch(leftJson, (json) => {
  if (!json) return;
  const editorElement = document.getElementById("monaco_editor") as HTMLElement;
  if (!editorElement) return;
  editorElement.innerHTML = "";
  const diffEditor = monaco.editor.createDiffEditor(editorElement, {
    enableSplitViewResizing: false,
    automaticLayout: true,
    readOnly: true,
    folding: true,
  });
  diffEditor.setModel({
    original: monaco.editor.createModel(leftJson.value, "json"),
    modified: monaco.editor.createModel(rightJson.value, "json"),
  });
  diffShow.value = true;
});

function rightRestore() {
  restore(rightDocId.value);
}

function leftRestore() {
  restore(leftDocId.value);
}

function restore(changeSetId: string) {
  const changeset = changeSets.value.find(
    (x) => x.id == changeSetId
  ) as ChangeSet;
  if (confirm(`Do you want to restore to the version '${changeset.label}'?`)) {
    show.value = true;

    const viewModel = {
      documentId: documentIdStr.value,
      documentType: documentType.value,
      changesetId: changeSetId,
      comment: "",
    } as RestoreChangeSet;

    restoreChangeset(viewModel, {
      onSuccess: () => {
        toast(`Restored successfully`, "Success", "success");

        const editorElement = document.getElementById("monaco_editor") as HTMLElement;
        editorElement.innerHTML = "";
        const diffEditor = monaco.editor.createDiffEditor(editorElement, {
          enableSplitViewResizing: false,
          automaticLayout: true,
          readOnly: true,
          folding: true,
        });
        diffEditor.setModel({
          original: monaco.editor.createModel("", "json"),
          modified: monaco.editor.createModel("", "json"),
        });

        documentIdChanged(null);
        diffShow.value = false;
      },
    });
  }
}

const toastTitle = ref("");
const toastBody = ref("");

function toast(message: string, title: string, variant: string) {
  toastTitle.value = title;
  toastBody.value = message;
  const toastLiveExample = document.getElementById("liveToast");
  const toast = new Toast(toastLiveExample as HTMLElement);
  toast.show();
}
</script>

<template>
  <main>
    <!-- Main Error -->

    <div
      id="liveToast"
          class="toast mb-4 mx-auto"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div class="toast-header">
            <strong class="me-auto">{{ toastTitle }}</strong>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="toast"
              aria-label="Close"
            ></button>
          </div>
          <div class="toast-body">
            {{ toastBody }}
          </div>
        </div>

        <PanelGrid :columns="1">
          <PanelCard data-cy="audit-mainpanel">
            <PageHeader
              title="Audit"
              :breadcrumbs="[
                { label: 'Dashboard', href: '/' },
                { label: 'Audit' }
              ]"
            />

            <div class="row my-3">
              <div class="col-sm-3">Document Type</div>
              <div class="col-sm-9">
                <VueMultiselect
                  id="documentTypeSelect"
                  ref="documentTypeSelect"
                  v-model="documentType"
                  :options="documentTypes"
                  :multiple="false"
                  placeholder="Select documentType"
                  :show-labels="false"
                  :allow-empty="false"
                  :loading="isLoadingDocumentTypes"
                  @open="openDocumentTypeMultiSelect($event)"
                  @select="documentTypeChanged($event)"
                >
                  <template #noOptions><span>No documentTypes</span></template>
                  <template #noResult
                    ><span>No documentTypes found</span></template
                  >
                </VueMultiselect>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-sm-3">Document</div>
              <div class="col-sm-9">
                <VueMultiselect
                  id="documentSelect"
                  ref="documentSelect"
                  v-model="documentId"
                  :options="documentIds"
                  :multiple="false"
                  placeholder="Select document"
                  track-by="id"
                  label="label"
                  :show-labels="false"
                  :allow-empty="false"
                  :loading="isLoadingDocumentIds"
                  @open="openDocumentIdMultiSelect($event)"
                  @select="documentIdChanged($event)"
                >
                  <template #noOptions><span>No documents</span></template>
                  <template #noResult><span>No documents found</span></template>
                </VueMultiselect>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-sm-3" id="divLabel">Changeset</div>
              <div class="col-sm-9">
                <VueMultiselect
                  id="changesetSelect"
                  ref="changesetSelect"
                  v-model="selectedChangeSets"
                  :options="changeSets"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Select changeset"
                  track-by="id"
                  label="label"
                  :max="2"
                  :show-labels="false"
                  :allow-empty="false"
                  :loading="isLoadingChangeSets"
                  @open="openChangesetMultiSelect($event)"
                  @select="selectedChangeSetChanged($event)"
                >
                  <template #noOptions><span>No changesets</span></template>
                  <template #noResult
                    ><span>No changesets found</span></template
                  >
                </VueMultiselect>
              </div>
            </div>

            <div v-show="diffShow" class="position-relative mt-4">
              <div v-if="isLoadingChangeSetDiff" class="overlay">
                <div class="overlay-content">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <button
                      class="bb-btn-secondary-cta btn-sm"
                      type="button"
                      @click="leftRestore"
                      v-show="leftRestoreShow"
                    >Restore</button>
                    <h2 class="mb-0 fs-6">{{ leftHeading }} <small class="text-muted fw-normal">{{ leftHeadingHelpText }}</small></h2>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <button
                      class="bb-btn-secondary-cta btn-sm"
                      type="button"
                      @click="rightRestore"
                      v-show="rightRestoreShow"
                      :disabled="rightRestoreDisabled"
                    >{{ rightRestoreText }}</button>
                    <h2 class="mb-0 fs-6">{{ rightHeading }} <small class="text-muted fw-normal">{{ rightHeadingHelpText }}</small></h2>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <a
                    id="showMetaLeft"
                    class=""
                    data-bs-toggle="collapse"
                    href="#collapseLeft"
                    role="button"
                    aria-expanded="false"
                    aria-controls="collapseLeft"
                  >
                    show metadata
                  </a>
                  <div class="collapse mt-3" id="collapseLeft">
                    <div class="card">
                      <div class="card-body">
                        <pre>{{ leftMetadata }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <a
                    class=""
                    data-bs-toggle="collapse"
                    href="#collapseRight"
                    role="button"
                    aria-expanded="false"
                    aria-controls="collapseRight"
                  >
                    show metadata
                  </a>
                  <div class="collapse mt-3" id="collapseRight">
                    <div class="card">
                      <div class="card-body">
                        <pre>{{ rightMetadata }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-sm-12">
                  <div class="editor" id="monaco_editor"></div>
                </div>
              </div>
            </div>
          </PanelCard>
        </PanelGrid>
  </main>
</template>

<style lang="scss">
.editor {
  height: 600px;
}
</style>
